import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useAlertLoadQuery, useAlertUpdateMutation } from "../../services/server/AlertApi";
import type { AlertEntity } from "../../types/AlertEntity";

export const AlertEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("alert");

    const [formData, setFormData] = useState<AlertEntity>({} as AlertEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useAlertLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.ALERTS}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAlertUpdateMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.id,
                        resource: "Alert",
                    }),
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<AlertEntity>
                    name="AlertEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
